import { Link } from "wouter";
import { PenLine, Shield, ShieldCheck, ShieldAlert } from "lucide-react";

export default function AiPrivacy() {
  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link href="/" className="flex items-center gap-2">
          <PenLine className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold tracking-tight text-card-foreground">ClassTrack</span>
        </Link>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-16">
        <div className="flex items-center gap-2 text-primary text-sm font-semibold uppercase tracking-wide mb-3">
          <Shield className="h-4 w-4" />
          AI &amp; student privacy
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">
          Registered student names are replaced before anything reaches the AI.
        </h1>
        <p className="text-lg text-gray-600 mb-10 leading-relaxed">
          ClassTrack uses AI to help you tidy up rough notes and plan lessons —
          without sending the AI provider your students&apos; names, your
          school&apos;s name, or the personal details that show up in classroom
          notes. This page explains exactly what gets sent, what doesn&apos;t,
          and why ClassTrack is safer than typing the same thing into ChatGPT.
        </p>

        <div className="prose prose-gray max-w-none space-y-10 text-gray-700 leading-relaxed">

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              What ClassTrack uses AI for
            </h2>
            <p>There are three AI-assisted features in ClassTrack, all optional:</p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li>
                <strong>Polish.</strong> Turns your shorthand observations into
                a tidier paragraph you can paste into a record.
              </li>
              <li>
                <strong>NCCD polish.</strong> The same idea, tuned for
                describing the adjustments you&apos;ve made for a student.
              </li>
              <li>
                <strong>Lesson prep.</strong> Generates a lesson outline based
                on the topic and class context you&apos;ve typed in.
              </li>
            </ul>
            <p className="mt-3">
              You only use these when you click the AI button. There&apos;s no
              background process that sends your notes anywhere.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              What gets sent to the AI provider
            </h2>
            <p>
              When you press an AI button, the text in that field passes
              through ClassTrack&apos;s server first. Before it leaves our
              server, the text is rewritten:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li>
                Every real student name is replaced with a placeholder like
                <code className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-xs">Student A</code>,
                <code className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-xs">Student B</code>.
              </li>
              <li>
                Phone numbers, email addresses, street addresses, dates,
                Medicare and NDIS numbers are replaced with neutral tokens
                like <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-xs">[phone]</code>,
                <code className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-xs">[email]</code>,
                <code className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-xs">[address]</code>.
              </li>
              <li>
                Parent/carer mentions (&quot;Mum Sarah called&quot;), titled
                people (&quot;Dr Walker&quot;), school names, common
                diagnoses and medications are also replaced.
              </li>
            </ul>
            <p className="mt-4">Here&apos;s what that looks like in practice:</p>
            <div className="not-prose mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                  What you typed
                </div>
                <p className="text-sm text-gray-800 leading-relaxed">
                  Liam was anxious during maths today. Mum Sarah called on
                  0411 222 333 to say his ADHD meds (Vyvanse) were missed.
                  Followed up 12/05/2026.
                </p>
              </div>
              <div className="rounded-lg border border-primary/30 p-4 bg-primary/5">
                <div className="text-xs uppercase tracking-wide text-primary font-semibold mb-2">
                  What the AI sees
                </div>
                <p className="text-sm text-gray-800 leading-relaxed font-mono">
                  Student A was anxious during maths today. [parent/carer]
                  called on [phone] to say his [diagnosis] meds
                  ([medication]) were missed. Followed up [date].
                </p>
              </div>
            </div>
            <p className="mt-4">
              When the AI sends back its polished version, ClassTrack&apos;s
              server swaps <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-xs">Student A</code> back
              to <em>Liam</em> on its way to your screen — so you read the
              real name, but the AI provider never did.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              What is explicitly NOT sent
            </h2>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>Real student first names, surnames, or full names.</li>
              <li>Your school&apos;s name.</li>
              <li>Parent and carer names.</li>
              <li>Phone numbers, email addresses, street addresses.</li>
              <li>NDIS and Medicare numbers.</li>
              <li>Specific diagnoses and medication names that ClassTrack recognises.</li>
              <li>Your unrelated student records — only the field you&apos;re working in.</li>
              <li>Your account email or your name.</li>
            </ul>
            <p className="mt-3">
              Redaction is best-effort pattern matching, not magic. If you
              type something the patterns don&apos;t cover, it could slip
              through. We&apos;re honest about that and keep tightening the
              patterns over time. The disclosure shown after every AI
              request lists exactly what was removed for that request, so
              you can see the redaction working in real time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              Which AI provider, and how long they keep it
            </h2>
            <p>
              ClassTrack uses <strong>OpenAI</strong> as its AI provider, via
              their developer API — <em>not</em> the ChatGPT product. This
              distinction matters: API requests have a different retention
              and training posture than ChatGPT conversations.
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li>
                Under OpenAI&apos;s API data policy, prompts sent through
                the API are <strong>not used to train OpenAI&apos;s models</strong>.
              </li>
              <li>
                OpenAI retains API requests for up to <strong>30 days</strong>
                {" "}for abuse monitoring, then deletes them.
              </li>
              <li>
                We plan to enable OpenAI&apos;s <strong>Zero Data Retention
                (ZDR)</strong> option before signing up the first school
                customer, which removes that 30-day window entirely.
              </li>
            </ul>
            <p className="mt-3">
              Even during that 30-day window, what OpenAI holds is the
              redacted text — placeholders and tokens — not real student
              names or identifying details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              How this compares to &quot;just use ChatGPT&quot;
            </h2>
            <p>
              Lots of teachers already paste rough notes into ChatGPT to
              tidy them up. It works, but it&apos;s a privacy problem your
              school probably hasn&apos;t signed off on. Here&apos;s the
              difference:
            </p>
            <div className="not-prose mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-amber-200 p-4 bg-amber-50">
                <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2">
                  <ShieldAlert className="h-4 w-4" />
                  Pasting into ChatGPT
                </div>
                <ul className="text-sm text-gray-800 space-y-1.5 list-disc pl-5">
                  <li>Real student names go to OpenAI as-typed.</li>
                  <li>Phone numbers, addresses, diagnoses go through unchanged.</li>
                  <li>Conversations may be used to improve OpenAI&apos;s models (depending on your settings).</li>
                  <li>Stored in your personal ChatGPT account history.</li>
                  <li>No audit trail showing what was shared.</li>
                </ul>
              </div>
              <div className="rounded-lg border border-emerald-200 p-4 bg-emerald-50">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2">
                  <ShieldCheck className="h-4 w-4" />
                  Using ClassTrack
                </div>
                <ul className="text-sm text-gray-800 space-y-1.5 list-disc pl-5">
                  <li>Names are replaced with <code className="px-1 py-0.5 rounded bg-white text-gray-700 font-mono text-xs">Student A</code> before leaving our server.</li>
                  <li>Phones, addresses, diagnoses, parent names are tokenised.</li>
                  <li>API requests are not used for training, by policy.</li>
                  <li>30-day retention today; ZDR planned before schools onboard.</li>
                  <li>Disclosure after every request shows what was removed.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              Your part of the deal
            </h2>
            <p>
              Redaction handles the patterns we recognise. The single best
              thing you can do as a teacher is keep notes professional and
              concise:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li>Use initials, preferred names, or student codes where you can.</li>
              <li>Skip detail that isn&apos;t needed for the record (full medical history, family circumstances, contact details).</li>
              <li>Check your school&apos;s policy on third-party tools before entering student data.</li>
            </ul>
            <p className="mt-3">
              ClassTrack shows an inline reminder in free-text fields to
              nudge you in this direction, and the disclosure shown after
              each AI request lets you see what got removed.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              More details
            </h2>
            <p>
              For the full legal version — including subprocessors, overseas
              disclosure, retention, and deletion — see our{" "}
              <Link href="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>{" "}
              and the{" "}
              <Link href="/legal/subprocessors" className="text-primary underline">
                subprocessors list
              </Link>. Questions?{" "}
              <a href="mailto:classtrack.com.au@gmail.com" className="text-primary underline">
                classtrack.com.au@gmail.com
              </a>.
            </p>
            <p className="mt-4 text-xs text-gray-400">
              ClassTrack is operated by The trustee for The ASSIDA Unit Trust, ABN 26 760 767 802, trading as ClassTrack.
            </p>
          </section>

        </div>
      </main>

      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <div className="flex justify-center gap-6 mb-3">
          <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link href="/ai-privacy" className="hover:text-white transition-colors">AI &amp; Privacy</Link>
          <Link href="/legal/subprocessors" className="hover:text-white transition-colors">Subprocessors</Link>
        </div>
        <p>&copy; 2026 ClassTrack. All rights reserved.</p>
      </footer>
    </div>
  );
}
